import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';
import {
  BeneficioElegibilidade,
  BeneficioOpcao,
  CombinacaoIdadeVinculacao,
  ConfiguracaoBeneficiarios,
  ElegibilidadeBeneficios,
  RegraImpedimento,
  RegrasGeraisElegibilidade,
  UnidadeTempo,
} from '../../../../core/models/parametros/beneficios.model';
import { BeneficiosService } from '../../../../core/services/parametros/beneficios.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  ANOS_MAXIMO,
  BENEFICIARIOS_MAXIMO,
  EXCECOES_ELEGIBILIDADE_MAXIMO,
  OBSERVACOES_ELEGIBILIDADE_MAXIMO,
  REGRAS_ELEGIBILIDADE_PADRAO,
  TEMPO_SERVICO_MAXIMO,
  UNIDADES_TEMPO_OPCOES,
  VALIDACOES_AUTOMATICAS,
  condicoesImpedimento,
  descreverRegras,
} from '../../../../core/utils/parametros/beneficios.defaults';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestSwitchValueAccessorDirective } from '../../../../shared/directives/switch-vivest/vivest-switch-value-accessor.directive';
import { ApiHttpError, resolveProblemMessage } from '../../../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { SituacoesCadastraisModalComponent } from '../../extrato-desligamento/situacoes-cadastrais-modal/situacoes-cadastrais-modal.component';
import { BeneficiosListaComponent } from '../beneficios-lista/beneficios-lista.component';
import { BeneficiosModalComponent } from '../beneficios-modal/beneficios-modal.component';
import { ImpedimentoEditado, ImpedimentoModalComponent } from '../impedimento-modal/impedimento-modal.component';

type AbaRegras = 'gerais' | 'excecao';

const ABAS_REGRAS: readonly { id: AbaRegras; rotulo: string }[] = [
  { id: 'gerais', rotulo: 'Regras Gerais' },
  { id: 'excecao', rotulo: 'Regras de Exceção' },
];

type CampoComErro = 'tempoMinimoServico' | 'quantidade' | 'percentualMinimo' | 'observacoes' | 'excecoes';

/** Campo em anos: inteiro de 0 a `ANOS_MAXIMO`. */
const VALIDADORES_ANOS = [Validators.min(0), Validators.max(ANOS_MAXIMO), Validators.pattern(/^\d+$/)];

const ERRO_ANOS = `Use um número inteiro de anos, de 0 a ${ANOS_MAXIMO}.`;

/** Uma combinação precisa exigir a idade, o tempo de vinculação ou os dois. */
function combinacaoPreenchida(grupo: AbstractControl): ValidationErrors | null {
  const { idadeMinima, tempoMinimoVinculacao } = grupo.value as CombinacaoIdadeVinculacao;
  return idadeMinima === null && tempoMinimoVinculacao === null ? { combinacaoVazia: true } : null;
}

/** Com todos os beneficiários no percentual mínimo, a soma não pode passar de 100%. */
function percentuaisCabem(grupo: AbstractControl): ValidationErrors | null {
  const { quantidade, percentualMinimo } = grupo.value as ConfiguracaoBeneficiarios;
  return quantidade !== null && percentualMinimo !== null && quantidade * percentualMinimo > 100
    ? { somaAcimaDe100: true }
    : null;
}

function criarCombinacao(fb: NonNullableFormBuilder, combinacao: CombinacaoIdadeVinculacao) {
  return fb.group(
    {
      idadeMinima: fb.control<number | null>(combinacao.idadeMinima, VALIDADORES_ANOS),
      tempoMinimoVinculacao: fb.control<number | null>(combinacao.tempoMinimoVinculacao, VALIDADORES_ANOS),
    },
    { validators: [combinacaoPreenchida] },
  );
}

/** O limite depende da unidade escolhida (120 anos ou 1440 meses). */
function tempoServicoNoLimite(form: AbstractControl): ValidationErrors | null {
  const { tempoMinimoServico, unidadeTempoServico } = form.value as {
    tempoMinimoServico: number | null;
    unidadeTempoServico: UnidadeTempo;
  };
  return tempoMinimoServico !== null && tempoMinimoServico > TEMPO_SERVICO_MAXIMO[unidadeTempoServico]
    ? { tempoServicoAcimaDoLimite: true }
    : null;
}

/**
 * Situações e impedimentos ficam fora do form, no rascunho, porque são editados por modal; as
 * comprovações também, porque as opções vêm do catálogo da API.
 */
function criarFormRegras(fb: NonNullableFormBuilder) {
  return fb.group({
    desligamentoObrigatorio: false,
    /** O limite depende da unidade e é conferido em `tempoServicoNoLimite`. */
    tempoMinimoServico: fb.control<number | null>(null, [Validators.min(0), Validators.pattern(/^\d+$/)]),
    unidadeTempoServico: 'anos' as UnidadeTempo,
    /** Alternativas: basta atender a uma. */
    combinacoes: fb.array<ReturnType<typeof criarCombinacao>>([]),
    beneficiarios: fb.group(
      {
        quantidade: fb.control<number | null>(null, [
          Validators.min(1),
          Validators.max(BENEFICIARIOS_MAXIMO),
          Validators.pattern(/^\d+$/),
        ]),
        percentualMinimo: fb.control<number | null>(null, [
          Validators.min(0.01),
          Validators.max(100),
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ]),
      },
      { validators: [percentuaisCabem] },
    ),
    observacoes: ['', Validators.maxLength(OBSERVACOES_ELEGIBILIDADE_MAXIMO)],
    excecoes: ['', Validators.maxLength(EXCECOES_ELEGIBILIDADE_MAXIMO)],
  }, { validators: [tempoServicoNoLimite] });
}

type FormRegras = ReturnType<typeof criarFormRegras>;

/** Preenche o formulário com o benefício, recriando as linhas de combinação. */
function preencher(fb: NonNullableFormBuilder, form: FormRegras, beneficio: BeneficioElegibilidade): void {
  const { regras } = beneficio;
  const { combinacoes } = form.controls;
  combinacoes.clear({ emitEvent: false });
  regras.combinacoes.forEach((combinacao) => combinacoes.push(criarCombinacao(fb, combinacao), { emitEvent: false }));
  form.reset(
    {
      desligamentoObrigatorio: regras.desligamentoObrigatorio,
      tempoMinimoServico: regras.tempoMinimoServico,
      unidadeTempoServico: regras.unidadeTempoServico,
      combinacoes: regras.combinacoes,
      beneficiarios: regras.beneficiarios,
      observacoes: regras.observacoes,
      excecoes: beneficio.excecoes,
    },
    { emitEvent: false },
  );
}

const fbValidacao = new FormBuilder().nonNullable;

/** Valida um benefício com as mesmas regras do formulário (para os benefícios fechados). */
function beneficioValido(beneficio: BeneficioElegibilidade): boolean {
  const form = criarFormRegras(fbValidacao);
  preencher(fbValidacao, form, beneficio);
  form.updateValueAndValidity();
  return form.valid && beneficio.regras.situacoesElegiveis.length > 0;
}

/** Regra aberta no modal de impedimento: `null` é uma regra nova. */
interface ImpedimentoEmEdicao {
  regra: RegraImpedimento | null;
}

/** Salvar e Cancelar valem só para o benefício aberto; ativar e excluir gravam na hora. */
@Component({
  selector: 'app-beneficios-elegibilidade',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    BeneficiosListaComponent,
    BeneficiosModalComponent,
    ImpedimentoModalComponent,
    SituacoesCadastraisModalComponent,
    VivestSwitchValueAccessorDirective,
    VivestModalAreaVisivelDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './beneficios-elegibilidade.component.html',
  styleUrl: './beneficios-elegibilidade.component.scss',
})
export class BeneficiosElegibilidadeComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly beneficiosService = inject(BeneficiosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly planoId = input.required<number>();
  readonly nomePlano = input('');
  /** Benefício aberto ao carregar; sem ele, abre o primeiro. */
  readonly beneficioInicial = input<number | null>(null);

  readonly abasRegras = ABAS_REGRAS;
  readonly unidadesTempo = UNIDADES_TEMPO_OPCOES;
  readonly validacoesAutomaticas = VALIDACOES_AUTOMATICAS;
  readonly excecoesMaximo = EXCECOES_ELEGIBILIDADE_MAXIMO;
  readonly observacoesMaximo = OBSERVACOES_ELEGIBILIDADE_MAXIMO;
  readonly anosMaximo = ANOS_MAXIMO;
  readonly beneficiariosMaximo = BENEFICIARIOS_MAXIMO;
  readonly descricaoModalSituacoes =
    'O participante pode solicitar o benefício se estiver em uma das situações escolhidas.';

  /** Último estado carregado ou salvo, para o "Cancelar". */
  private readonly carregado = signal<ElegibilidadeBeneficios | null>(null);
  /** O que está na tela, de todos os benefícios. */
  readonly rascunho = signal<ElegibilidadeBeneficios | null>(null);
  readonly selecionadoId = signal<number | null>(null);
  readonly abaRegras = signal<AbaRegras>('gerais');

  /** Situações do catálogo, na ordem da tela. */
  readonly opcoesSituacao = computed(() => this.rascunho()?.situacoes ?? []);
  /** Comprovações do catálogo, na ordem da tela. */
  readonly comprovacoesOpcoes = computed(() => this.rascunho()?.comprovacoes ?? []);

  /** Inclui as desmarcadas ainda não salvas: ficam na tela até salvar ou descartar. */
  readonly situacoesVisiveis = signal<number[]>([]);
  readonly selecionandoSituacoes = signal(false);
  readonly impedimentoEmEdicao = signal<ImpedimentoEmEdicao | null>(null);

  readonly beneficioAExcluir = signal<BeneficioElegibilidade | null>(null);
  readonly descricaoExclusao = computed(() => {
    const beneficio = this.beneficioAExcluir();
    return beneficio
      ? `${beneficio.nome} e as regras dele saem do plano. Essa ação não pode ser desfeita.`
      : '';
  });

  readonly adicionando = signal(false);
  readonly catalogo = signal<BeneficioOpcao[]>([]);
  readonly carregandoCatalogo = signal(false);

  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);
  /** Depois da primeira tentativa de salvar, todos os erros ficam visíveis. */
  readonly tentouSalvar = signal(false);

  /** Parâmetros do benefício escolhido na lista. */
  readonly form = criarFormRegras(this.fb);

  readonly beneficios = computed(() => this.rascunho()?.beneficios ?? []);
  readonly selecionado = computed(() => this.beneficios().find((item) => item.id === this.selecionadoId()) ?? null);
  readonly idsIncluidos = computed(() => this.beneficios().map((item) => item.id));
  private readonly nomes = computed(() => new Map(this.beneficios().map((item) => [item.id, item.nome])));

  /** Os outros benefícios do plano, que podem impedir o pedido do benefício aberto. */
  readonly outrosBeneficios = computed<BeneficioOpcao[]>(() =>
    this.beneficios()
      .filter((item) => item.id !== this.selecionadoId())
      .map(({ id, nome }) => ({ id, nome })),
  );

  /** Diferentes do último estado salvo, ou novos; ficam marcados na lista para não se perderem. */
  readonly idsAlterados = computed(() => {
    const salvos = new Map((this.carregado()?.beneficios ?? []).map((item) => [item.id, JSON.stringify(item)]));
    return new Set(this.beneficios().filter((item) => salvos.get(item.id) !== JSON.stringify(item)).map((item) => item.id));
  });

  /** Adicionados e nunca salvos: só existem na tela até o primeiro Salvar. */
  readonly idsNovos = computed(() => {
    const salvos = new Set((this.carregado()?.beneficios ?? []).map((item) => item.id));
    return new Set(this.beneficios().filter((item) => !salvos.has(item.id)).map((item) => item.id));
  });

  /** Edição não salva no benefício aberto: habilita Salvar e Cancelar. */
  readonly temAlteracoes = computed(() => {
    const id = this.selecionadoId();
    return !!id && this.idsAlterados().has(id) && !this.salvando();
  });

  readonly selecionadoInvalido = computed(() => {
    const beneficio = this.selecionado();
    return !!beneficio && !beneficioValido(beneficio);
  });

  /** Resumo do benefício aberto no formato da matriz de elegibilidade. */
  readonly resumo = computed(() => {
    const beneficio = this.selecionado();
    const rascunho = this.rascunho();
    return {
      condicoes: beneficio && rascunho ? descreverRegras(beneficio.regras, this.nomes(), rascunho) : [],
      excecoes: beneficio?.excecoes.trim() ?? '',
    };
  });

  /** Nenhuma situação marcada no benefício aberto, depois de tentar salvar. */
  readonly erroSituacoes = computed(() =>
    this.tentouSalvar() && this.selecionado()?.regras.situacoesElegiveis.length === 0
      ? 'Marque ao menos uma situação cadastral.'
      : '',
  );

  ngOnInit(): void {
    // Cada alteração do formulário vai para o benefício escolhido no rascunho.
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const { excecoes, ...regras } = this.form.getRawValue();
      this.alterarSelecionado((beneficio) => ({
        ...beneficio,
        regras: { ...beneficio.regras, ...regras },
        excecoes,
      }));
    });

    this.loaderService.showLoader();
    this.beneficiosService
      .obterElegibilidade(this.planoId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (elegibilidade) => {
          this.aplicar(elegibilidade);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar a elegibilidade dos benefícios.', 'error');
        },
      });
  }

  selecionar(id: number, aba: AbaRegras = 'gerais'): void {
    const beneficio = this.beneficios().find((item) => item.id === id);
    if (!beneficio) {
      return;
    }
    // Salvar vale por benefício: os erros de uma tentativa anterior não seguem para o próximo.
    this.tentouSalvar.set(false);
    this.selecionadoId.set(id);
    this.abaRegras.set(aba);
    this.situacoesVisiveis.set([...beneficio.regras.situacoesElegiveis]);
    preencher(this.fb, this.form, beneficio);
  }

  /** Já criado: grava na hora, sem depender do Salvar. Novo: só muda a tela (vai no Salvar). Se falhar, volta. */
  ativar(id: number, ativo: boolean): void {
    const comAtivo = (config: ElegibilidadeBeneficios, valor: boolean): ElegibilidadeBeneficios => ({
      ...config,
      beneficios: config.beneficios.map((item) => (item.id === id ? { ...item, ativo: valor } : item)),
    });
    this.alterar((config) => comAtivo(config, ativo));

    const salvo = this.carregado()?.beneficios.find((item) => item.id === id);
    if (!salvo) {
      return;
    }
    this.loaderService.showLoader();
    this.beneficiosService
      .alterarAutoatendimento(this.planoId(), id, ativo)
      .pipe(
        finalize(() => this.loaderService.hideLoader()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          const carregado = this.carregado();
          if (carregado) {
            this.carregado.set(comAtivo(carregado, ativo));
          }
          this.toastService.show(`Benefício ${salvo.nome} ${ativo ? 'ativado' : 'desativado'}.`, 'success');
        },
        error: (erro: unknown) => {
          this.alterar((config) => comAtivo(config, !ativo));
          this.toastService.show(
            mensagemDoErro(erro, `Não foi possível ${ativo ? 'ativar' : 'desativar'} o benefício ${salvo.nome}.`),
            'error',
          );
        },
      });
  }

  pedirExclusao(beneficio: BeneficioElegibilidade): void {
    this.beneficioAExcluir.set(beneficio);
  }

  pedirExclusaoPorId(id: number): void {
    const beneficio = this.beneficios().find((item) => item.id === id);
    if (beneficio) {
      this.pedirExclusao(beneficio);
    }
  }

  /**
   * Lixeira gravada na hora (nunca salvo só sai da tela); edições dos outros seguem pendentes. A API
   * guarda as regras para quando o benefício voltar ao plano.
   */
  confirmarExclusao(): void {
    const excluido = this.beneficioAExcluir();
    const carregado = this.carregado();
    this.beneficioAExcluir.set(null);
    if (!excluido || !carregado || this.salvando()) {
      return;
    }
    if (!carregado.beneficios.some((item) => item.id === excluido.id)) {
      this.removerDaTela(excluido.id);
      return;
    }

    this.salvando.set(true);
    this.loaderService.showLoader();
    this.beneficiosService
      .removerDoPlano(this.planoId(), excluido.id)
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          const atual = this.carregado();
          if (atual) {
            this.carregado.set(semBeneficio(atual, excluido.id));
          }
          this.removerDaTela(excluido.id);
          this.toastService.show(`Benefício ${excluido.nome} excluído.`, 'success');
        },
        error: (erro: unknown) =>
          this.toastService.show(
            mensagemDoErro(erro, `Não foi possível excluir o benefício ${excluido.nome}. Tente novamente.`),
            'error',
          ),
      });
  }

  /** Tira o benefício do rascunho e, se ele estava aberto, abre o primeiro da lista. */
  private removerDaTela(id: number): void {
    this.alterar((config) => semBeneficio(config, id));
    if (id === this.selecionadoId()) {
      const primeiro = this.beneficios()[0];
      if (primeiro) {
        this.selecionar(primeiro.id);
      } else {
        this.selecionadoId.set(null);
      }
    }
  }

  /** O catálogo é buscado na primeira abertura. */
  abrirAdicionar(): void {
    this.adicionando.set(true);
    if (this.catalogo().length || this.carregandoCatalogo()) {
      return;
    }
    this.carregandoCatalogo.set(true);
    this.loaderService.showLoader();
    this.beneficiosService
      .listarBeneficiosDisponiveis()
      .pipe(
        finalize(() => {
          this.carregandoCatalogo.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (beneficios) => this.catalogo.set(beneficios),
        error: () => {
          this.adicionando.set(false);
          this.toastService.show('Não foi possível carregar os benefícios disponíveis.', 'error');
        },
      });
  }

  /** Entram ativos, sem exigências definidas, e o primeiro é aberto. */
  adicionarBeneficios(opcoes: BeneficioOpcao[]): void {
    this.adicionando.set(false);
    const novos = opcoes.filter((opcao) => !this.idsIncluidos().includes(opcao.id));
    if (!novos.length) {
      return;
    }
    this.alterar((config) => ({
      ...config,
      beneficios: [
        ...config.beneficios,
        ...novos.map((opcao) => ({
          id: opcao.id,
          nome: opcao.nome,
          ativo: true,
          regras: structuredClone(REGRAS_ELEGIBILIDADE_PADRAO),
          excecoes: '',
        })),
      ],
    }));
    this.selecionar(novos[0].id);
  }

  selecionarAbaRegras(aba: AbaRegras): void {
    this.abaRegras.set(aba);
  }

  adicionarCombinacao(): void {
    this.form.controls.combinacoes.push(criarCombinacao(this.fb, { idadeMinima: null, tempoMinimoVinculacao: null }));
  }

  removerCombinacao(indice: number): void {
    this.form.controls.combinacoes.removeAt(indice);
  }

  rotuloSituacao(situacao: number): string {
    return this.opcoesSituacao().find((opcao) => opcao.value === situacao)?.label ?? String(situacao);
  }

  situacaoMarcada(situacao: number): boolean {
    return !!this.selecionado()?.regras.situacoesElegiveis.includes(situacao);
  }

  /** Desmarcar tira a situação das regras, mas ela continua na tela até salvar ou descartar. */
  marcarSituacao(situacao: number, marcada: boolean): void {
    this.alterarSituacoes((situacoes) => {
      if (marcada) {
        situacoes.add(situacao);
      } else {
        situacoes.delete(situacao);
      }
    });
  }

  /** Entram marcadas na lista. */
  adicionarSituacoes(situacoes: number[]): void {
    this.situacoesVisiveis.set(naOrdemDaTela(this.opcoesSituacao(), new Set([...this.situacoesVisiveis(), ...situacoes])));
    this.alterarSituacoes((marcadas) => situacoes.forEach((situacao) => marcadas.add(situacao)));
    this.selecionandoSituacoes.set(false);
  }

  comprovacaoMarcada(comprovacao: number): boolean {
    return !!this.selecionado()?.regras.comprovacoes.includes(comprovacao);
  }

  /** Comprovações marcadas na ordem das opções da tela. */
  marcarComprovacao(comprovacao: number, marcada: boolean): void {
    this.alterarRegras((regras) => {
      const comprovacoes = new Set(regras.comprovacoes);
      if (marcada) {
        comprovacoes.add(comprovacao);
      } else {
        comprovacoes.delete(comprovacao);
      }
      return { ...regras, comprovacoes: naOrdemDaTela(this.comprovacoesOpcoes(), comprovacoes) };
    });
  }

  condicoesImpedimento(regra: RegraImpedimento): string[] {
    return condicoesImpedimento(regra, this.nomes());
  }

  adicionarImpedimento(): void {
    this.impedimentoEmEdicao.set({ regra: null });
  }

  editarImpedimento(regra: RegraImpedimento): void {
    this.impedimentoEmEdicao.set({ regra });
  }

  excluirImpedimento(id: string): void {
    this.alterarImpedimentos((regras) => regras.filter((regra) => regra.id !== id));
  }

  salvarImpedimento(editado: ImpedimentoEditado): void {
    const edicao = this.impedimentoEmEdicao();
    if (!edicao) {
      return;
    }
    this.alterarImpedimentos((regras) =>
      edicao.regra
        ? regras.map((regra) => (regra.id === edicao.regra!.id ? { ...regra, ...editado } : regra))
        : [...regras, { id: novoId(), idApi: null, ...editado }],
    );
    this.impedimentoEmEdicao.set(null);
  }

  /** Só depois de mexer no campo ou tentar salvar. */
  private visivel(controle: AbstractControl): boolean {
    return controle.touched || this.tentouSalvar();
  }

  erro(campo: CampoComErro): string {
    const { beneficiarios } = this.form.controls;
    const controle =
      campo === 'quantidade' || campo === 'percentualMinimo' ? beneficiarios.controls[campo] : this.form.controls[campo];
    const foraDoLimite = campo === 'tempoMinimoServico' && this.form.hasError('tempoServicoAcimaDoLimite');
    if ((controle.valid && !foraDoLimite) || !this.visivel(controle)) {
      return '';
    }
    switch (campo) {
      case 'quantidade':
        return `Use um número inteiro de 1 a ${BENEFICIARIOS_MAXIMO}.`;
      case 'percentualMinimo':
        return 'Use um percentual de 0,01 a 100, com até duas casas decimais.';
      case 'observacoes':
        return `Use no máximo ${OBSERVACOES_ELEGIBILIDADE_MAXIMO} caracteres.`;
      case 'excecoes':
        return `Use no máximo ${EXCECOES_ELEGIBILIDADE_MAXIMO} caracteres.`;
      default: {
        const unidade = this.form.controls.unidadeTempoServico.value;
        return `Use um número inteiro de 0 a ${TEMPO_SERVICO_MAXIMO[unidade]} ${unidade}.`;
      }
    }
  }

  /** Quantidade × percentual mínimo acima de 100%: os beneficiários não cabem. */
  erroSomaBeneficiarios(): string {
    const grupo = this.form.controls.beneficiarios;
    return grupo.hasError('somaAcimaDe100') && this.visivel(grupo)
      ? 'Com essa quantidade, o percentual mínimo de cada beneficiário soma mais de 100%.'
      : '';
  }

  erroCombinacao(indice: number): string {
    const grupo = this.form.controls.combinacoes.at(indice);
    if (!grupo || grupo.valid || !this.visivel(grupo)) {
      return '';
    }
    return grupo.hasError('combinacaoVazia') ? 'Informe a idade, o tempo de vinculação ou os dois.' : ERRO_ANOS;
  }

  /** Valida só o benefício aberto e pede confirmação; a gravação acontece em `confirmarSalvar`. */
  salvar(): void {
    this.tentouSalvar.set(true);
    this.form.markAllAsTouched();
    if (!this.temAlteracoes()) {
      return;
    }
    const beneficio = this.selecionado();
    if (beneficio && this.selecionadoInvalido()) {
      const aba: AbaRegras = beneficio.excecoes.length > EXCECOES_ELEGIBILIDADE_MAXIMO ? 'excecao' : 'gerais';
      this.abaRegras.set(aba);
      this.toastService.show(`Revise as regras de ${beneficio.nome} antes de salvar.`, 'error');
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  cancelarSalvar(): void {
    this.confirmandoSalvar.set(false);
  }

  /**
   * Grava só o benefício aberto (um PUT, que também cria ou reativa a linha do plano); edições dos
   * outros seguem pendentes. O que a API devolve substitui o benefício na tela, com os ids das regras novas.
   */
  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    const beneficio = this.selecionado();
    if (!beneficio || !this.carregado() || this.selecionadoInvalido() || this.salvando()) {
      return;
    }
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.beneficiosService
      .salvarBeneficio(this.planoId(), beneficio)
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salvo) => {
          this.aplicarSalvo(salvo);
          this.toastService.show(`Regras de ${beneficio.nome} salvas.`, 'success');
        },
        error: (erro: unknown) =>
          this.toastService.show(
            mensagemDoErro(erro, `Não foi possível salvar as regras de ${beneficio.nome}. Tente novamente.`),
            'error',
          ),
      });
  }

  /** O benefício gravado entra no rascunho e no último estado salvo, na ordem da tela. */
  private aplicarSalvo(salvo: BeneficioElegibilidade): void {
    const carregado = this.carregado();
    const rascunho = this.rascunho();
    if (!carregado || !rascunho) {
      return;
    }
    const salvos = new Map(carregado.beneficios.map((item) => [item.id, item]));
    salvos.set(salvo.id, salvo);
    this.rascunho.set({
      ...rascunho,
      beneficios: rascunho.beneficios.map((item) => (item.id === salvo.id ? structuredClone(salvo) : item)),
    });
    this.carregado.set({
      ...carregado,
      // Benefícios novos e ainda não salvos (exceto o gravado) ficam de fora.
      beneficios: rascunho.beneficios.filter((item) => salvos.has(item.id)).map((item) => structuredClone(salvos.get(item.id)!)),
    });
    if (this.selecionadoId() === salvo.id) {
      this.selecionar(salvo.id, this.abaRegras());
    }
  }

  /** Desfaz só o benefício aberto; se ele nunca foi salvo, sai da lista. */
  descartar(): void {
    const id = this.selecionadoId();
    const salvo = this.carregado()?.beneficios.find((item) => item.id === id);
    if (!id) {
      return;
    }
    if (!salvo) {
      this.removerDaTela(id);
      return;
    }
    this.alterar((config) => ({
      ...config,
      beneficios: config.beneficios.map((item) => (item.id === id ? structuredClone(salvo) : item)),
    }));
    this.selecionar(id, this.abaRegras());
  }

  /** Mantém o benefício aberto, se ele ainda existir; senão abre o pedido ou o primeiro. */
  private aplicar(elegibilidade: ElegibilidadeBeneficios): void {
    this.carregado.set(structuredClone(elegibilidade));
    this.rascunho.set(structuredClone(elegibilidade));
    this.tentouSalvar.set(false);
    const procurado = this.selecionadoId() ?? this.beneficioInicial();
    const aberto = elegibilidade.beneficios.find((item) => item.id === procurado) ?? elegibilidade.beneficios[0];
    if (aberto) {
      this.selecionar(aberto.id, this.abaRegras());
    } else {
      this.selecionadoId.set(null);
    }
  }

  private alterar(mudanca: (config: ElegibilidadeBeneficios) => ElegibilidadeBeneficios): void {
    const atual = this.rascunho();
    if (atual) {
      this.rascunho.set(mudanca(atual));
    }
  }

  private alterarSelecionado(mudanca: (beneficio: BeneficioElegibilidade) => BeneficioElegibilidade): void {
    const id = this.selecionadoId();
    this.alterar((config) => ({
      ...config,
      beneficios: config.beneficios.map((item) => (item.id === id ? mudanca(item) : item)),
    }));
  }

  private alterarRegras(mudanca: (regras: RegrasGeraisElegibilidade) => RegrasGeraisElegibilidade): void {
    this.alterarSelecionado((beneficio) => ({ ...beneficio, regras: mudanca(beneficio.regras) }));
  }

  private alterarSituacoes(mudanca: (situacoes: Set<number>) => void): void {
    this.alterarRegras((regras) => {
      const situacoes = new Set(regras.situacoesElegiveis);
      mudanca(situacoes);
      return { ...regras, situacoesElegiveis: naOrdemDaTela(this.opcoesSituacao(), situacoes) };
    });
  }

  private alterarImpedimentos(mudanca: (regras: RegraImpedimento[]) => RegraImpedimento[]): void {
    this.alterarRegras((regras) => ({ ...regras, impedimentos: mudanca(regras.impedimentos) }));
  }
}

/**
 * Mensagem da API (400, 404, 409: campo inválido, benefício fora do plano, duplicidade) ou, sem ela,
 * a mensagem padrão da tela.
 */
function mensagemDoErro(erro: unknown, padrao: string): string {
  return erro instanceof ApiHttpError && erro.status >= 400 && erro.status < 500 && erro.status !== 401
    ? resolveProblemMessage(erro.problem, padrao)
    : padrao;
}

/** Também tira o benefício dos impedimentos dos outros; regra sem condição é removida. */
function semBeneficio(config: ElegibilidadeBeneficios, id: number): ElegibilidadeBeneficios {
  return {
    ...config,
    beneficios: config.beneficios
      .filter((item) => item.id !== id)
      .map((item) => ({
        ...item,
        regras: {
          ...item.regras,
          impedimentos: item.regras.impedimentos
            .map((regra) => ({ ...regra, beneficios: regra.beneficios.filter((outro) => outro !== id) }))
            .filter((regra) => regra.beneficios.length || regra.idadeMinima !== null || regra.idadeMaxima !== null),
        },
      })),
  };
}

/** Ids na ordem da lista de opções, não na ordem em que foram escolhidos. */
function naOrdemDaTela(opcoes: readonly { value: number }[], ids: ReadonlySet<number>): number[] {
  return opcoes.map((opcao) => opcao.value).filter((id) => ids.has(id));
}

let sequencia = 0;

/** Id local de uma regra nova; a API passará a gerar o id definitivo. */
function novoId(): string {
  sequencia += 1;
  return `impedimento-${Date.now()}-${sequencia}`;
}
